import { pageTitle } from '@palaccount/config';
import {
  API_KEY_MESSAGES,
  INTEGRATION_LINK,
  KEY_SCOPES,
  type ApiKeyRow,
  type IntegrationState,
  type KeyScope,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  Cloud,
  CreditCard,
  KeyRound,
  Mail,
  Receipt,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api } from '@/api';
import { ConfirmBox } from '@/components/confirm-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { useCan } from '@/lib/permissions';
import { localizeServerMessage, throwApiError } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

/**
 * ADM-02 — INTEGRATIONS & API KEYS (§15.7).
 *
 * ── THE INTEGRATION CARDS REPORT; THEY DO NOT SWITCH ──────────────────────
 *
 * There is no **Connect** and no **Disconnect**, and that is the wave's main
 * conclusion rather than an omission. §7 admits what the prototype's button
 * does — "what it must really do is open that service's sign-in; THE PRODUCT
 * ONLY FLIPS A SWITCH" — and every one of the six is deploy-time vendor
 * wiring this product holds no credentials for (§8.7 payments, §8.10
 * e-invoicing, §2.12 email, §2.15 storage; the bank feed and webhooks have no
 * adapter at all). A stored `connected` boolean with nothing behind it is
 * precisely the `multi_branch` defect the acceptance sweep found — "one of
 * three paid modules is a switch wired to nothing" — and this screen would
 * have built six of them.
 *
 * So each card reports what the DEPLOYMENT actually has, names the provider
 * when there is one (so `mock` is visible as `mock`), and says where a
 * connection is really made. §5's three deep links are kept, because the
 * screens they point at are real.
 *
 * ── TWO RULES THE MISSING CONTROL TAKES WITH IT — RETIRED, D52 ─────────────
 *
 * The paragraph above argued the absence and named no rule, so two ids sat in
 * the coverage queue looking like untouched work. Both describe DISCONNECTING,
 * and disconnecting is the half of the control that does not exist either:
 *   BR-ADM-02-06  — rule-coverage:ignore — "Disconnecting an integration must
 *             warn what will stop working, and must not delete any data that
 *             integration brought in." BOTH clauses need the act to be
 *             possible. Nothing on this screen, and no route behind it, can
 *             perform it: `/api/integrations` is a GET, and the other three
 *             routes are the key table's.
 *   ERR-ADM-02-03 — rule-coverage:ignore — "Disconnected the Email
 *             integration" → "Invoices, statements and reminder emails will
 *             stop being sent." The same act stated as a §16 error row. Its
 *             sentence exists nowhere in the product, and should not be added
 *             to a card that cannot cause the condition it describes.
 *
 * Delegated ruling D52 (30 Aug 2026) retired both, reversibly, under A13's
 * reading — A13 kept the integration Disconnect out of release 1, and these
 * two govern only that act. They leave the queue by "Retired by delegated
 * ruling D52" notices at their own spec rows (finalnotices--spec.patch), not
 * by citations here: the marks stay, because this screen enforces nothing of
 * either — a message naming a control the build does not have must not be
 * cited even when the reasoning behind the absence is sound (ADM-07's shape
 * on a second screen). Without the marks this paragraph would COUNT as citing
 * the two ids whose retirement it exists to explain.
 *
 * The reversal condition, in the ruling's own words: a Disconnect act
 * landing, with its own off-state store. It is not a UI change alone — five
 * of the six cards read deploy-time vendor configuration (§8.7, §8.10,
 * §2.12, §2.15) that this application does not own, so a Disconnect would
 * need somewhere of its own to store the off state; a stored switch nothing
 * reads is the `multi_branch` defect the header rejects.
 *
 * ── THE KEYS ARE THE REAL HALF ─────────────────────────────────────────────
 *
 * Minted here, shown once (BR-ADM-02-01), scoped (BR-ADM-02-04), revoked
 * through §8's shared confirmation box (BR-ADM-02-03), and revocable ALL AT
 * ONCE for §17's "a key leaks", which the prototype has no answer to.
 */

/** §5's six, with the icons the spec names. */
const INTEGRATION_ICON: Record<string, LucideIcon> = {
  payment_gateway: CreditCard,
  einvoicing: Receipt,
  bank_feed: Banknote,
  email: Mail,
  webhooks: Webhook,
  cloud_storage: Cloud,
};

const KEY = ['integrations'] as const;

function useIntegrations() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api.apiKeys.list();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

export function IntegrationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useIntegrations();
  // BR-ADM-02-07 names three acts; the read is `admin.view` and the two writes
  // are `admin.edit`, so an Accountant can review the credentials that exist
  // and cannot mint or destroy one.
  const canEdit = useCan('admin.edit');

  const [name, setName] = useState('');
  const [scope, setScope] = useState<KeyScope>('read');
  const [minted, setMinted] = useState<{ token: string } | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('integrations.title'));
  }, [t]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: KEY });

  const create = useMutation({
    mutationFn: async (input: { name: string; scope: KeyScope }) => {
      const res = await api.apiKeys.create({ body: input });
      if (res.status !== 201) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: (body) => {
      // BR-ADM-02-01's one moment. Held in component state and nowhere else —
      // never in the query cache, which would survive navigation and could be
      // read back after the box was closed.
      setMinted({ token: body.token });
      setName('');
      invalidate();
    },
  });

  const revoke = useMutation({
    mutationFn: async (keyId: string) => {
      const res = await api.apiKeys.revoke({ params: { keyId }, body: {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      setRevoking(null);
      invalidate();
    },
  });

  const revokeAll = useMutation({
    mutationFn: async () => {
      const res = await api.apiKeys.revokeAll({ body: {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      setRevokingAll(false);
      invalidate();
    },
  });

  if (isPending) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{t('shell.loading')}</p>;
  }

  const connected = (data?.integrations ?? []).filter((row) => row.connected).length;
  const keys = data?.keys ?? [];

  return (
    <div className="grid gap-4">
      {/* §4's summary line: "n connected · m API keys". */}
      <p className="text-sm text-muted-foreground">
        {t('integrations.summary', { connected, keys: keys.length })}
      </p>

      {/* §4: six cards, three across. */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {(data?.integrations ?? []).map((row) => (
          <IntegrationCard key={row.integration} state={row} />
        ))}
      </div>

      <Card>
        <CardContent className="grid gap-3 p-6">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <KeyRound className="size-4" aria-hidden />
              {t('integrations.keysTitle')}
            </h2>
            <p className="w-full text-xs text-muted-foreground">{t('integrations.keysNote')}</p>
          </div>

          {/* §17's last edge case, which the prototype has no answer to: "A key
              leaks. There must be a way to revoke every key at once." */}
          {canEdit && keys.length > 0 ? (
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => setRevokingAll(true)}>
                {t('integrations.revokeAll')}
              </Button>
            </div>
          ) : null}

          {canEdit ? (
            <form
              className="flex flex-wrap items-end gap-2 rounded-md border border-border p-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim()) create.mutate({ name: name.trim(), scope });
              }}
            >
              <label className="grid gap-1 text-xs text-muted-foreground">
                {t('integrations.keyName')}
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t('integrations.keyNamePh')}
                  aria-label={t('integrations.keyName')}
                  required
                />
              </label>
              <label className="grid gap-1 text-xs text-muted-foreground">
                {t('integrations.scope')}
                <NativeSelect
                  value={scope}
                  onChange={(event) => setScope(event.target.value as KeyScope)}
                  aria-label={t('integrations.scope')}
                >
                  {KEY_SCOPES.map((option) => (
                    <option key={option} value={option}>
                      {t(`integrations.scope.${option}`)}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <Button type="submit" disabled={create.isPending}>
                {t('integrations.newKey')}
              </Button>
            </form>
          ) : null}

          {keys.length === 0 ? (
            // §17's Empty row — "a line saying so, not a headerless table".
            <p className="text-xs text-muted-foreground">{t('integrations.noKeys')}</p>
          ) : (
            <ul className="grid gap-2">
              {keys.map((key) => (
                <li
                  key={key.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3 py-2"
                >
                  <span className="grid">
                    <span className="text-sm font-medium">{key.name}</span>
                    {/* §4: "stacks the key's name over its MASKED PREFIX in the
                        tabular typeface". Pinned LTR — a key is a code (N-10). */}
                    <span dir="ltr" className="font-mono text-xs tabular-nums text-muted-foreground">
                      {key.prefix}
                    </span>
                  </span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px]">
                    {t(`integrations.scope.${key.scope}`)}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {key.createdAt.slice(0, 10)}
                  </span>
                  {/* BR-ADM-02-08 — "A key that has never been used must show a
                      DASH rather than a date." */}
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {key.lastUsedAt ? key.lastUsedAt.slice(0, 10) : '—'}
                  </span>
                  {/* BR-ADM-02-09's flag — decided as a flag and not an expiry;
                      see the contract for why a key must not stop working at
                      3am on a schedule nobody is watching. */}
                  {key.stale ? (
                    <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                      {t('integrations.stale')}
                    </span>
                  ) : null}
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="ms-auto"
                      onClick={() => setRevoking(key)}
                    >
                      {t('integrations.revoke')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/*
        BR-ADM-02-01's one moment, and ERR-ADM-02-02's warning on the way out.
        The value is in component state only: it was never in the query cache,
        so closing this really is the last time it exists here.
      */}
      <ConfirmBox
        open={minted !== null}
        title={t('integrations.keyMade')}
        body={
          <span className="grid gap-3">
            {/*
              The secret gets its own element: it is a CODE, so it is pinned
              LTR and set in the tabular face like every other code in the
              product (N-9/N-10), and it is selectable on its own rather than
              buried in a paragraph somebody has to drag across. `break-all`
              because a 43-character token must wrap inside 420 pixels rather
              than widen the box.
            */}
            <code
              dir="ltr"
              className="select-all break-all rounded-md bg-muted p-3 font-mono text-xs text-foreground"
            >
              {minted?.token ?? ''}
            </code>
            {/* Through the map, not raw. ERR-ADM-02-02's wording is the
                contract's, so the constant stays imported and the key is
                computed from it — a reword of the contract moves the key with
                it, which a hand-written `t()` would let drift. */}
            <span>{localizeServerMessage(t, API_KEY_MESSAGES.copyWarning)}</span>
          </span>
        }
        confirmLabel={t('integrations.copied')}
        onConfirm={() => setMinted(null)}
        onCancel={() => setMinted(null)}
      />

      {/* §8's box, with §8's own body text. BR-ADM-02-03. */}
      <ConfirmBox
        open={revoking !== null}
        title={t('integrations.revokeTitle')}
        body={localizeServerMessage(t, API_KEY_MESSAGES.revokeWarning)}
        confirmLabel={t('integrations.revoke')}
        busy={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
        onCancel={() => setRevoking(null)}
      />

      <ConfirmBox
        open={revokingAll}
        title={t('integrations.revokeAllTitle')}
        body={t('integrations.revokeAllBody')}
        confirmLabel={t('integrations.revokeAll')}
        busy={revokeAll.isPending}
        onConfirm={() => revokeAll.mutate()}
        onCancel={() => setRevokingAll(false)}
      />
    </div>
  );
}

function IntegrationCard({ state }: { state: IntegrationState }) {
  const { t } = useTranslation();
  const Icon = INTEGRATION_ICON[state.integration] ?? Webhook;
  const link = INTEGRATION_LINK[state.integration];

  return (
    <Card>
      <CardContent className="grid gap-2 p-4">
        <span
          className={cn(
            'flex size-[42px] items-center justify-center rounded-lg',
            state.connected ? 'bg-primary/12 text-foreground' : 'bg-muted text-muted-foreground',
          )}
          aria-hidden
        >
          <Icon className="size-5" />
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            {t(`integrations.name.${state.integration}`)}
          </span>
          {/* §9's pill, with its dot. Colour is never the only signal — the
              pill carries its own word (§21.17). */}
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]',
              state.connected
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'size-1.5 rounded-full',
                state.connected ? 'bg-emerald-600' : 'bg-muted-foreground',
              )}
              aria-hidden
            />
            {t(state.connected ? 'integrations.connected' : 'integrations.notConnected')}
          </span>
        </span>
        <p className="text-xs text-muted-foreground">
          {t(`integrations.desc.${state.integration}`)}
        </p>
        {/* Which vendor, when there is one. A green pill alone cannot tell a
            real connection from a development one, and `mock` is a fact an
            administrator on a live deployment needs. */}
        {state.provider ? (
          <p className="text-[11px] text-muted-foreground">
            {t('integrations.provider', { provider: state.provider })}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            {localizeServerMessage(t, API_KEY_MESSAGES.wiredAtDeployment)}
          </p>
        )}
        {link ? (
          <Link to={link} className="text-xs underline">
            {t(`integrations.link.${state.integration}`)}
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}
